import { Card, Grid, Icon, Stack, Typography } from '@mui/material';
import { AttendanceCode } from '@schooly/api';
import { Spin } from '@schooly/style';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAttendanceCodes } from '../../../../hooks/useAttendanceCodes';
import { useNotificationSettings } from '../../../../hooks/useNotificationSettings';
import { SchoolNotificationsAttendanceContent } from './SchoolNotificationsAttendanceContent';

export const SchoolNotificationsAttendance: FC = () => {
  const {
    attendanceNotificationsEnabled,
    selectedCodes,
    notificationSettings,
    setSelectedCodes,
    onCodesSubmit,
    onToggleSwitch,
    loadingNotificationSettings,
    loadingUpdateNotificationSettings,
  } = useNotificationSettings();

  const [changesCompleted, setChangesCompleted] = useState<boolean>();

  const { attendanceCodes } = useAttendanceCodes();

  const codesDisplayedSelected = useMemo(() => {
    const justificationCodeIds = attendanceCodes
      .filter((code) => code.has_justification)
      .map((code) => code.id);
    const codesDisplayedSelectedIds = [...selectedCodes, ...justificationCodeIds];

    return attendanceCodes.filter((code) => codesDisplayedSelectedIds.includes(code.id));
  }, [attendanceCodes, selectedCodes]);

  const onSubmit = useCallback(
    async (enabled: boolean) => {
      return await onCodesSubmit(enabled);
    },
    [onCodesSubmit],
  );

  const onSwitch = useCallback(
    async (enabled: boolean) => {
      return await onToggleSwitch(enabled);
    },
    [onToggleSwitch],
  );

  const handleSelectCode = useCallback(
    async (code: AttendanceCode) => {
      if (code.has_justification) return;

      setSelectedCodes((codes) => {
        const isSelected = codes.some((id) => id === code.id);
        return isSelected ? codes.filter((id) => id !== code.id) : [...codes, code.id];
      });
    },
    [setSelectedCodes],
  );

  return (
    <Card sx={{ overflowX: 'auto' }}>
      <Grid container spacing={3} p={2.5}>
        <Grid item md={3} sm={12}>
          <Typography variant="h2">
            <FormattedMessage id="attendance-Title" />
          </Typography>
        </Grid>

        {loadingNotificationSettings ? (
          <Stack justifyContent="center" alignItems="center" width="100%">
            <Icon fontSize="medium">
              <Spin />
            </Icon>
          </Stack>
        ) : (
          <Grid item md={9} sm={12}>
            <SchoolNotificationsAttendanceContent
              onSubmit={onSubmit}
              onSwitch={onSwitch}
              notificationEmailsProvided={
                !!notificationSettings?.internal_emails?.admissions_email &&
                !!notificationSettings?.internal_emails?.invoicing_email
              }
              attendanceCodes={attendanceCodes}
              selectedCodes={codesDisplayedSelected}
              notificationsEnabled={attendanceNotificationsEnabled}
              onSelectCode={handleSelectCode}
              updating={loadingUpdateNotificationSettings}
              changesCompleted={changesCompleted}
              setChangesCompleted={setChangesCompleted}
            />
          </Grid>
        )}
      </Grid>
    </Card>
  );
};
